/**
 * Utility functions for date formatting and manipulation
 */

/**
 * Format a date string to "Day Month Year" format (e.g., "7 August 2024")
 * @param {string} dateString - Date string in ISO format (YYYY-MM-DD)
 * @returns {string} Formatted date string
 */
export const formatDateToLong = (dateString) => {
  if (!dateString) return 'Not specified';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original string if invalid date
    }
    
    // Format the date
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Return original string on error
  }
};

/**
 * Calculate age from date of birth
 * @param {string} dateOfBirth - Date of birth in ISO format (YYYY-MM-DD)
 * @returns {string} Age in years and months or months
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return 'Unknown age';
  
  try {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    
    // Check if date is valid
    if (isNaN(birthDate.getTime())) {
      return 'Unknown age';
    }
    
    let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
    months -= birthDate.getMonth();
    months += today.getMonth();
    
    // Adjust for day of month
    if (today.getDate() < birthDate.getDate()) {
      months--;
    }
    
    if (months < 0) {
      return 'Not born yet';
    }
    
    if (months < 24) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    }
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    }
    
    return `${years} year${years !== 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
  } catch (error) {
    console.error('Error calculating age:', error);
    return 'Unknown age';
  }
};
